// ─── Base MetricRecord ────────────────────────────────────────────────────────
import { getMetricUnit } from "@/metrics/metric-display"
import { MetricRecord } from "@/metrics/runtime"
import { normalizeMetricRecordData } from "@/metrics/runtime/record-data"

import { logger } from "@/lib/logger"
import type { MetricRecordData } from "@/types/metrics/contracts"

import { ApiStateMetricRecord } from "../records/ApiStateMetricRecord"
import { CategoricalMetricRecord } from "../records/base/CategoricalMetricRecord"
// ─── Type Bases ───────────────────────────────────────────────────────────────
import { NumericalMetricRecord } from "../records/base/NumericalMetricRecord"
import { SetMetricRecord } from "../records/base/SetMetricRecord"
// ─── Binary ───────────────────────────────────────────────────────────────────
import { AgentFailsToRespondMetricRecord } from "../records/binary/AgentFailsToRespondMetricRecord"
import { AgentJudgeMetricRecord } from "../records/binary/AgentJudgeMetricRecord"
import { AgentNeedsRepromptingMetricRecord } from "../records/binary/AgentNeedsRepromptingMetricRecord"
import { AgentRefusalMetricRecord } from "../records/binary/AgentRefusalMetricRecord"
import { AgentRepeatsItselfMetricRecord } from "../records/binary/AgentRepeatsItselfMetricRecord"
import { AudioBinaryLLMMetricRecord } from "../records/binary/AudioBinaryLLMMetricRecord"
import { BinaryLLMMetricRecord } from "../records/binary/BinaryLLMMetricRecord"
// ─── Custom Configurable Variants ────────────────────────────────────────────
import { CustomAgentFailsToRespondMetricRecord } from "../records/binary/CustomAgentFailsToRespondMetricRecord"
import { CustomAgentNeedsRepromptingMetricRecord } from "../records/binary/CustomAgentNeedsRepromptingMetricRecord"
import { CustomAudioSentimentMetricRecord } from "../records/binary/CustomAudioSentimentMetricRecord"
import { CustomEndReasonMetricRecord } from "../records/binary/CustomEndReasonMetricRecord"
import { TranscriptRegexMatchMetricRecord } from "../records/binary/TranscriptRegexMatchMetricRecord"
import { UserFrustrationMetricRecord } from "../records/binary/UserFrustrationMetricRecord"
import { WordsPerMessageWithThresholdMetricRecord } from "../records/binary/WordsPerMessageWithThresholdMetricRecord"
import { AgentJudgeCategoricalMetricRecord } from "../records/categorical/AgentJudgeCategoricalMetricRecord"
// ─── Categorical ──────────────────────────────────────────────────────────────
import { AudioCategoricalLLMMetricRecord } from "../records/categorical/AudioCategoricalLLMMetricRecord"
// ─── Categorical (audio) ─────────────────────────────────────────────────────
import { AudioSentimentMetricRecord } from "../records/categorical/AudioSentimentMetricRecord"
import { CategoricalLLMMetricRecord } from "../records/categorical/CategoricalLLMMetricRecord"
import { DerivedStrictLogicMetricRecord } from "../records/categorical/DerivedStrictLogicMetricRecord"
import { EndReasonMetricRecord } from "../records/categorical/EndReasonMetricRecord"
import { MatchExpectedSimWrapperMetricRecord } from "../records/categorical/MatchExpectedSimWrapperMetricRecord"
import { SentimentAnalysisMetricRecord } from "../records/categorical/SentimentAnalysisMetricRecord"
import { CompositeEvaluationMetricRecord } from "../records/CompositeEvaluationMetricRecord"
import { CustomTraceValuesMetricRecord } from "../records/CustomTraceValuesMetricRecord"
import { LLMTextMetricRecord } from "../records/LLMTextMetricRecord"
import { MetadataFieldMetricMetricRecord } from "../records/MetadataFieldMetricMetricRecord"
import { AbruptPitchChangesMetricRecord } from "../records/numerical/AbruptPitchChangesMetricRecord"
import { AgentJudgeNumericalMetricRecord } from "../records/numerical/AgentJudgeNumericalMetricRecord"
import { AudioDurationMetricRecord } from "../records/numerical/AudioDurationMetricRecord"
import { AudioFrequencyMetricRecord } from "../records/numerical/AudioFrequencyMetricRecord"
// ─── Numerical ────────────────────────────────────────────────────────────────
import { AudioNumericalLLMMetricRecord } from "../records/numerical/AudioNumericalLLMMetricRecord"
import { ClippingArtifactMetricRecord } from "../records/numerical/ClippingArtifactMetricRecord"
import { CodecArtifactMetricRecord } from "../records/numerical/CodecArtifactMetricRecord"
import { CustomAudioFrequencyMetricRecord } from "../records/numerical/CustomAudioFrequencyMetricRecord"
import { CustomTraceMetricRecord } from "../records/numerical/CustomTraceMetricRecord"
import { DerivedAggregateMetricRecord } from "../records/numerical/DerivedAggregateMetricRecord"
import { DropoutArtifactMetricRecord } from "../records/numerical/DropoutArtifactMetricRecord"
import { InterruptionRateMetricRecord } from "../records/numerical/InterruptionRate"
import { IvrFlowAdherenceMetricRecord } from "../records/numerical/IvrFlowAdherenceMetricRecord"
import { LatencyMetricRecord } from "../records/numerical/LatencyMetricRecord"
import { LoopDetectionMetricRecord } from "../records/numerical/LoopDetectionMetricRecord"
import { LufsMetricRecord } from "../records/numerical/LufsMetricRecord"
import { MusicDetectionMetricRecord } from "../records/numerical/MusicDetectionMetricRecord"
import { NonExpressivePausesMetricRecord } from "../records/numerical/NonExpressivePausesMetricRecord"
import { NumericalLLMMetricRecord } from "../records/numerical/NumericalLLMMetricRecord"
import { PauseAnalysisMetricRecord } from "../records/numerical/PauseAnalysisMetricRecord"
import { PhonemeStretchMetricRecord } from "../records/numerical/PhonemeStretchMetricRecord"
import { PitchVariabilityMetricRecord } from "../records/numerical/PitchVariabilityMetricRecord"
import { RubricDecisionTreeMetricRecord } from "../records/numerical/RubricDecisionTreeMetricRecord"
import { SignalToNoiseMetricRecord } from "../records/numerical/SignalToNoiseMetricRecord"
import { SpeakingTimePercentageMetricRecord } from "../records/numerical/SpeakingTimePercentageMetricRecord"
import { SpectrogramPitchAnalysisMetricRecord } from "../records/numerical/SpectrogramPitchAnalysisMetricRecord"
import { SpeechArtifactAnomalyMetricRecord } from "../records/numerical/SpeechArtifactAnomalyMetricRecord"
import { SpeechTempoMetricRecord } from "../records/numerical/SpeechTempoMetricRecord"
import { SqlFloatMetricRecord } from "../records/numerical/SqlMetricRecord"
import { SyllableRateMetricRecord } from "../records/numerical/SyllableRateMetricRecord"
import { TerminalContourMetricRecord } from "../records/numerical/TerminalContourMetricRecord"
import { TimbreDriftMetricRecord } from "../records/numerical/TimbreDriftMetricRecord"
import { TimeToFirstAudioMetricRecord } from "../records/numerical/TimeToFirstAudioMetricRecord"
import { TranscriptionErrorMetricRecord } from "../records/numerical/TranscriptionErrorMetricRecord"
import { VocalFryManagerMetricRecord } from "../records/numerical/VocalFryMetricRecord"
import { VoiceQualityMetricRecord } from "../records/numerical/VoiceQualityMetricRecord"
import { VolumePitchMisalignmentMetricRecord } from "../records/numerical/VolumePitchMisalignmentMetricRecord"
import { VolumeVarianceMetricRecord } from "../records/numerical/VolumeVarianceMetricRecord"
import { WordsPerMessageMetricRecord } from "../records/numerical/WordsPerMessageMetricRecord"
import {
  PythonBinaryMetricRecord,
  PythonNumericalMetricRecord,
} from "../records/PythonMetricRecord"
import { METRIC_MANAGERS } from "./metricManagerIds"

const log = logger("createMetricInstance")

export const createMetricInstance = (data: MetricRecordData): MetricRecord => {
  switch (data.metric_manager) {
    // ── Binary ──────────────────────────────────────────────────────────────
    case METRIC_MANAGERS.manager_agentic_llm_judge:
      return new AgentJudgeMetricRecord(data)
    case METRIC_MANAGERS.manager_llm_binary:
      return new BinaryLLMMetricRecord(data)
    case METRIC_MANAGERS.manager_audio_llm_binary:
      return new AudioBinaryLLMMetricRecord(data)
    case METRIC_MANAGERS.manager_agent_fails_to_respond:
      return new AgentFailsToRespondMetricRecord(data)
    case METRIC_MANAGERS.manager_agent_needs_reprompting:
      return new AgentNeedsRepromptingMetricRecord(data)
    case METRIC_MANAGERS.manager_agent_repeats_itself:
      return new AgentRepeatsItselfMetricRecord(data)
    case METRIC_MANAGERS.manager_transcript_regex_match:
      return new TranscriptRegexMatchMetricRecord(data)
    case METRIC_MANAGERS.manager_user_frustration:
      return new UserFrustrationMetricRecord(data)
    case METRIC_MANAGERS.manager_agent_refusal:
      return new AgentRefusalMetricRecord(data)
    case METRIC_MANAGERS.manager_words_per_message_with_threshold:
      return new WordsPerMessageWithThresholdMetricRecord(data)

    // ── Categorical ─────────────────────────────────────────────────────────
    case METRIC_MANAGERS.manager_audio_llm_categorical:
      return new AudioCategoricalLLMMetricRecord(data)
    case METRIC_MANAGERS.manager_default_categorical:
      return new CategoricalLLMMetricRecord(data)
    case METRIC_MANAGERS.manager_agentic_llm_judge_categorical:
      return new AgentJudgeCategoricalMetricRecord(data)
    case METRIC_MANAGERS.manager_derived_strict_logic:
      return new DerivedStrictLogicMetricRecord(data)
    case METRIC_MANAGERS.manager_end_reason:
      return new EndReasonMetricRecord(data)
    case METRIC_MANAGERS.manager_llm_text:
      return new LLMTextMetricRecord(data)
    case METRIC_MANAGERS.manager_sentimentanalysis:
      return new SentimentAnalysisMetricRecord(data)
    case METRIC_MANAGERS.manager_match_expected_sim_wrapper:
      return new MatchExpectedSimWrapperMetricRecord(data)

    // ── Numerical ───────────────────────────────────────────────────────────
    case METRIC_MANAGERS.manager_audio_llm_numerical:
      return new AudioNumericalLLMMetricRecord(data)
    case METRIC_MANAGERS.interruption_rate_manager:
      return new InterruptionRateMetricRecord(data)
    case METRIC_MANAGERS.manager_persona_interruption_rate:
      return new InterruptionRateMetricRecord(data)
    case METRIC_MANAGERS.manager_latency:
      return new LatencyMetricRecord(data)
    case METRIC_MANAGERS.manager_persona_latency:
      return new LatencyMetricRecord(data)
    case METRIC_MANAGERS.manager_numerical_llm_judge:
      return new NumericalLLMMetricRecord(data)
    case METRIC_MANAGERS.manager_agentic_llm_judge_numerical:
      return new AgentJudgeNumericalMetricRecord(data)
    case METRIC_MANAGERS.manager_signal_to_noise_ratio:
      return new SignalToNoiseMetricRecord(data)
    case METRIC_MANAGERS.manager_time_to_first_audio:
      return new TimeToFirstAudioMetricRecord(data)
    case METRIC_MANAGERS.manager_transcription_error:
      return new TranscriptionErrorMetricRecord(data)
    case METRIC_MANAGERS.manager_words_per_message:
      return new WordsPerMessageMetricRecord(data)
    case METRIC_MANAGERS.manager_abrupt_pitch_changes:
      return new AbruptPitchChangesMetricRecord(data)
    case METRIC_MANAGERS.manager_pitch_variability:
      return new PitchVariabilityMetricRecord(data)
    case METRIC_MANAGERS.manager_lufs:
      return new LufsMetricRecord(data)
    case METRIC_MANAGERS.manager_terminal_contour:
      return new TerminalContourMetricRecord(data)
    case METRIC_MANAGERS.audio_duration_metric_manager:
      return new AudioDurationMetricRecord(data)
    case METRIC_MANAGERS.manager_audio_frequency:
      return new AudioFrequencyMetricRecord(data)
    case METRIC_MANAGERS.manager_clipping_artifact:
      return new ClippingArtifactMetricRecord(data)
    case METRIC_MANAGERS.manager_codec_artifact:
      return new CodecArtifactMetricRecord(data)
    case METRIC_MANAGERS.manager_persona_clipping_artifact:
      return new ClippingArtifactMetricRecord(data)
    case METRIC_MANAGERS.manager_persona_codec_artifact:
      return new CodecArtifactMetricRecord(data)
    case METRIC_MANAGERS.manager_dropout_artifact:
      return new DropoutArtifactMetricRecord(data)
    case METRIC_MANAGERS.manager_non_expressive_pauses:
      return new NonExpressivePausesMetricRecord(data)
    case METRIC_MANAGERS.manager_persona_non_expressive_pauses:
      return new NonExpressivePausesMetricRecord(data)
    case METRIC_MANAGERS.pause_analysis_metric:
      return new PauseAnalysisMetricRecord(data)
    case METRIC_MANAGERS.manager_phonemes_per_second:
      return new SpeechTempoMetricRecord(data)
    case METRIC_MANAGERS.manager_phoneme_stretch:
      return new PhonemeStretchMetricRecord(data)
    case METRIC_MANAGERS.manager_persona_phoneme_stretch:
      return new PhonemeStretchMetricRecord(data)
    case METRIC_MANAGERS.manager_spectrogram_pitch_analysis:
      return new SpectrogramPitchAnalysisMetricRecord(data)
    case METRIC_MANAGERS.manager_vocal_fry:
      return new VocalFryManagerMetricRecord(data)
    case METRIC_MANAGERS.manager_volume_pitch_misalignment:
      return new VolumePitchMisalignmentMetricRecord(data)
    case METRIC_MANAGERS.manager_volume:
      return new VolumeVarianceMetricRecord(data)
    case METRIC_MANAGERS.manager_loop_detection:
      return new LoopDetectionMetricRecord(data)
    case METRIC_MANAGERS.manager_music_detection:
      return new MusicDetectionMetricRecord(data)
    case METRIC_MANAGERS.manager_persona_volume_pitch_misalignment:
      return new VolumePitchMisalignmentMetricRecord(data)
    case METRIC_MANAGERS.manager_persona_volume:
      return new VolumeVarianceMetricRecord(data)
    case METRIC_MANAGERS.manager_persona_loop_detection:
      return new LoopDetectionMetricRecord(data)
    case METRIC_MANAGERS.manager_persona_music_detection:
      return new MusicDetectionMetricRecord(data)
    case METRIC_MANAGERS.manager_syllable_rate:
      return new SyllableRateMetricRecord(data)
    case METRIC_MANAGERS.manager_voice_quality:
      return new VoiceQualityMetricRecord(data)
    case METRIC_MANAGERS.manager_timbre_drift:
      return new TimbreDriftMetricRecord(data)
    case METRIC_MANAGERS.manager_speech_artifact_anomaly:
      return new SpeechArtifactAnomalyMetricRecord(data)
    case METRIC_MANAGERS.manager_speaking_time_percentage:
      return new SpeakingTimePercentageMetricRecord(data)

    // ── Categorical (audio) ────────────────────────────────────────────────
    case METRIC_MANAGERS.manager_audio_sentiment:
      return new AudioSentimentMetricRecord(data)

    // ── Base MetricRecord ───────────────────────────────────────────────────
    case METRIC_MANAGERS.manager_api_state:
      return new ApiStateMetricRecord(data)
    case METRIC_MANAGERS.manager_composite_evaluation:
      return new CompositeEvaluationMetricRecord(data)
    case METRIC_MANAGERS.manager_metadata_field_metric:
      return new MetadataFieldMetricMetricRecord(data)
    case METRIC_MANAGERS.manager_custom_trace:
      return data.metric_metadata?.aggregation_method === "values"
        ? new CustomTraceValuesMetricRecord(data)
        : new CustomTraceMetricRecord(data)
    case METRIC_MANAGERS.manager_sql_float:
      return new SqlFloatMetricRecord(data)
    case METRIC_MANAGERS.manager_python: {
      if (data.output_type === "string") {
        return new PythonBinaryMetricRecord(data)
      }
      const record = new PythonNumericalMetricRecord(data)
      record.unit = getMetricUnit(data)
      record.aggregate_units = record.unit
      return record
    }
    case METRIC_MANAGERS.manager_derived_aggregate:
      return new DerivedAggregateMetricRecord(data)
    case METRIC_MANAGERS.manager_rubric_decision_tree:
      return new RubricDecisionTreeMetricRecord(data)
    case METRIC_MANAGERS.manager_ivr_flow_adherence:
      return new IvrFlowAdherenceMetricRecord(data)
    case METRIC_MANAGERS.manager_custom_audio_frequency:
      return new CustomAudioFrequencyMetricRecord(data)
    case METRIC_MANAGERS.manager_custom_end_reason:
      return new CustomEndReasonMetricRecord(data)
    case METRIC_MANAGERS.manager_custom_agent_needs_reprompting:
      return new CustomAgentNeedsRepromptingMetricRecord(data)
    case METRIC_MANAGERS.manager_custom_audio_sentiment:
      return new CustomAudioSentimentMetricRecord(data)
    case METRIC_MANAGERS.manager_custom_agent_fails_to_respond:
      return new CustomAgentFailsToRespondMetricRecord(data)

    default:
      if (data.output_type === "float") {
        return new NumericalMetricRecord(data)
      }
      if (data.output_type === "string") {
        return new CategoricalMetricRecord(data)
      }
      if (data.output_type === "set") {
        return new SetMetricRecord(data)
      }
      log.warn(
        "Falling back to base MetricRecord for unrecognized output_type; compareValues will throw if called",
        {
          output_type: data.output_type,
          metric_manager: data.metric_manager,
          id: data.id,
        },
      )
      return new MetricRecord(data)
  }
}

export const convertResponseToMetricRecord = (
  response: unknown,
): MetricRecord => {
  return createMetricInstance(normalizeMetricRecordData(response))
}
